/**
 * DisconnectedConnectionBanner - shows when a tab's connection is offline
 *
 * Displays a warning banner at the top of the query editor with options to
 * reconnect or remove the connection from the session.
 */

import { useState } from 'react';
import { AlertTriangle, RefreshCw, X } from 'lucide-react';
import { Button } from '../../../components/ui/button';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
interface DisconnectedConnectionBannerProps {
  /** Name of the disconnected connection */
  connectionName: string;
  /** Error message from failed connection attempt */
  error?: string;
  /** Called when user clicks "Try Reconnecting" */
  onReconnect: () => Promise<boolean>;
  /** Called when user dismisses the banner */
  onDismiss?: () => void;
}

export function DisconnectedConnectionBanner({
  connectionName,
  error,
  onReconnect,
  onDismiss,
}: DisconnectedConnectionBannerProps) {
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [reconnectError, setReconnectError] = useState<string | null>(null);

  const handleReconnect = async () => {
    setIsReconnecting(true);
    setReconnectError(null);

    try {
      const success = await onReconnect();
      if (!success) {
        setReconnectError('Failed to reconnect. Please try again.');
      }
      // If successful, the banner will be removed by parent component
    } catch (err) {
      setReconnectError(
        err instanceof Error ? err.message : 'Failed to reconnect'
      );
    } finally {
      setIsReconnecting(false);
    }
  };

  const displayError = reconnectError || error;

  return (
    <div
      className="flex items-start gap-3 px-4 py-3 bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-800"
      data-testid="disconnected-connection-banner"
    >
      <AlertTriangle className="h-5 w-5 text-warning flex-shrink-0 mt-0.5" />

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
          This query is for a disconnected connection.
        </p>
        <p className="text-xs text-warning mt-0.5">
          The connection "{connectionName}" is not available.
          {displayError && (
            <span className="block mt-1 text-amber-700 dark:text-amber-300">
              {displayError}
            </span>
          )}
        </p>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        <Button
          variant="outline"
          size="sm"
          onClick={handleReconnect}
          disabled={isReconnecting}
          className="h-7 text-xs"
          data-testid="reconnect-button"
        >
          {isReconnecting ? (
            <RandomDotmSpinner size={14} className="mr-1.5" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
          )}
          {isReconnecting ? 'Reconnecting...' : 'Try Reconnecting'}
        </Button>

        {onDismiss && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onDismiss}
            className="h-7 w-7 p-0"
            title="Dismiss"
            data-testid="dismiss-banner-button"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
